import React, { useCallback, useEffect, useState } from "react";
import { Camera, CheckCircle2, X } from "lucide-react";
import { api, errorMessage, fileUrl } from "../api/client";
import type { Inspection, ManualResponse, Paged, Part } from "../api/types";
import { useSession } from "../store/session";
import { Button, Card, cx, Field, Input, PageHeader, Select, Skeleton } from "../components/Controls";
import { DemoTag, VerdictBadge } from "../components/Badge";
import { toast } from "../components/Toast";
import { APP_VERSION, fmtTime, getDeviceId } from "../lib/format";

const BATCH_KEY = "insp.batch_code";

function Recent({ version, operatorId }: { version: number; operatorId?: string }) {
  const [items, setItems] = useState<Inspection[] | null>(null);
  useEffect(() => {
    api.get<Paged<Inspection>>("/inspections", { limit: 5, operator_id: operatorId }).then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [version, operatorId]);
  return (
    <Card className="p-4" data-testid="register-recent-list">
      <h3 className="mb-2 font-display text-sm font-semibold">Últimas 5 inspecciones</h3>
      {!items ? <Skeleton className="h-20" /> : items.length === 0 ? <p className="text-sm text-slate-400">Todavía no hay inspecciones.</p> : (
        <div className="divide-y divide-slate-100">
          {items.map((i) => (
            <div key={i.id} className="flex items-center justify-between py-2 text-sm" data-testid="register-recent-item">
              <span className="font-mono text-xs text-slate-500">{fmtTime(i.timestamp)}</span>
              <span>{i.part?.part_number} · Punto {i.point_index + 1}</span>
              <span className="inline-flex items-center gap-1.5">{i.is_demo && <DemoTag />}<VerdictBadge value={i.operator_verdict} /></span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function RegisterPage() {
  const { session, refreshMe } = useSession();
  const isAdmin = session.operator.role === "admin";
  const [parts, setParts] = useState<Part[]>([]);
  const [partId, setPartId] = useState<string | null>(isAdmin ? null : session.operator.assigned_part_id);
  const [part, setPart] = useState<Part | null>(null);
  const [checked, setChecked] = useState(isAdmin);
  const [point, setPoint] = useState(0);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [batch, setBatch] = useState(localStorage.getItem(BATCH_KEY) || "");
  const [busy, setBusy] = useState<"OK" | "NG" | null>(null);
  const [complete, setComplete] = useState(false);
  const [done, setDone] = useState<number[]>([]);
  const [pieces, setPieces] = useState(0);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      api.get<Part[]>("/parts", { active: "true" }).then((ps) => {
        setParts(ps);
        setPartId((cur) => cur || ps[0]?.id || null);
      }).catch(() => undefined);
    } else {
      refreshMe().then((op) => setPartId(op?.assigned_part_id || null)).catch(() => undefined).finally(() => setChecked(true));
    }
  }, [isAdmin, refreshMe]);

  useEffect(() => {
    setPart(null);
    setPoint(0);
    setComplete(false);
    setDone([]);
    if (partId) api.get<Part>(`/parts/${partId}`).then(setPart).catch(() => undefined);
  }, [partId]);

  const clearPhoto = useCallback(() => {
    setPhoto(null);
    setPreview(null);
  }, []);

  const save = async (verdict: "OK" | "NG") => {
    if (!part) return;
    setBusy(verdict);
    const fd = new FormData();
    fd.append("part_id", part.id);
    fd.append("point_index", String(point));
    fd.append("verdict", verdict);
    fd.append("device_id", getDeviceId());
    fd.append("app_version", APP_VERSION);
    if (batch.trim()) fd.append("batch_code", batch.trim());
    if (photo) fd.append("image", photo);
    try {
      const r = await api.form<ManualResponse>("POST", "/inspections/manual", fd);
      toast.success(`Guardada · Punto ${r.point_index + 1} · ${verdict === "OK" ? "Buena" : "Mala"}`);
      clearPhoto();
      setVersion((v) => v + 1);
      const nextDone = done.includes(point) ? done : [...done, point];
      const pending = part.points.map((_, i) => i).filter((i) => !nextDone.includes(i));
      if (!pending.length) {
        setComplete(true);
        setDone([]);
        setPieces((n) => n + 1);
      } else {
        setDone(nextDone);
        setPoint(pending.find((i) => i > point) ?? pending[0]);
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const noPart = !isAdmin && checked && !partId;
  return (
    <div className="mx-auto max-w-2xl animate-rise space-y-4">
      <PageHeader title="Registrar" subtitle={`Piezas completas en esta sesión: ${pieces}`} />
      {isAdmin && (
        <Field label="Pieza">
          <Select value={partId || ""} onChange={(e) => setPartId(e.target.value || null)} data-testid="register-part-select">
            {parts.length === 0 && <option value="">Sin piezas activas</option>}
            {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} · {p.name}</option>)}
          </Select>
        </Field>
      )}
      {noPart && <Card className="p-6 text-center text-slate-600" data-testid="register-no-part">Pedile al supervisor que te asigne una pieza.</Card>}
      {partId && !part && <Skeleton className="h-64" />}
      {part && (
        <Card data-testid="register-card">
          {part.reference_image_url && <div className="reticle overflow-hidden rounded-t-xl bg-graphite"><img src={fileUrl(part.reference_image_url)} alt="Referencia" className="max-h-28 w-full object-contain sm:max-h-56" data-testid="register-reference-image" /></div>}
          <div className="space-y-4 p-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-slate-500">{part.part_number}</span>
              {part.is_demo && <DemoTag />}
            </div>
            {complete ? (
              <div className="space-y-3 py-6 text-center" data-testid="register-piece-complete">
                <CheckCircle2 className="mx-auto h-12 w-12 text-ok" />
                <div className="font-display text-3xl font-bold">Pieza completa</div>
                <div className="text-sm text-slate-500" data-testid="register-session-counter">{pieces} {pieces === 1 ? "pieza" : "piezas"} en esta sesión</div>
                <Button onClick={() => { setComplete(false); setPoint(0); }} data-testid="register-next-piece-button">Siguiente pieza</Button>
              </div>
            ) : (
              <>
                <div className="font-display text-2xl font-bold" data-testid="register-point-title">Punto {point + 1} de {part.points.length} · {part.points[point]}</div>
                <div className="flex flex-wrap gap-2">
                  {part.points.map((p, i) => (
                    <button key={i} onClick={() => setPoint(i)} className={cx("rounded-full border px-3 py-1.5 text-sm font-medium transition-colors", i === point ? "border-ink bg-ink text-white" : done.includes(i) ? "border-ok/40 bg-ok-soft text-ok" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50")} data-testid={`register-point-chip-${i}`}>{i + 1} · {p}</button>
                  ))}
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr,1fr]">
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-3 text-sm text-slate-600 hover:bg-slate-50" data-testid="register-photo-label">
                    <Camera className="h-4 w-4" /> {photo ? "Cambiar foto" : "Foto (opcional)"}
                    <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPhoto(f); setPreview(URL.createObjectURL(f)); } e.target.value = ""; }} data-testid="register-photo-input" />
                  </label>
                  <Input placeholder="Lote (opcional)" value={batch} onChange={(e) => { setBatch(e.target.value); localStorage.setItem(BATCH_KEY, e.target.value); }} data-testid="register-batch-input" />
                </div>
                {preview && (
                  <div className="relative inline-block">
                    <img src={preview} alt="Vista previa" className="max-h-40 rounded" data-testid="register-photo-preview" />
                    <button className="absolute right-1 top-1 rounded-full bg-ink/80 p-1 text-white" onClick={clearPhoto} aria-label="Quitar foto" data-testid="register-photo-remove"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <div className="sticky bottom-14 z-10 -mx-4 grid grid-cols-2 gap-3 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:static lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
                  <Button variant="success" size="lg" loading={busy === "OK"} disabled={!!busy} onClick={() => save("OK")} data-testid="register-good-button">Buena</Button>
                  <Button variant="danger" size="lg" loading={busy === "NG"} disabled={!!busy} onClick={() => save("NG")} data-testid="register-bad-button">Mala</Button>
                </div>
              </>
            )}
            <p className="text-xs text-slate-400">Registro sin IA: se guarda tu decisión a tu nombre. La IA se suma en la próxima entrega.</p>
          </div>
        </Card>
      )}
      <Recent version={version} operatorId={isAdmin ? session.operator.id : undefined} />
    </div>
  );
}
